<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <div class="d-flex align-items-start justify-content-between mb-2">
          <div>
            <h5 class="mb-25">
              <feather-icon
                icon="GridIcon"
                size="18"
                class="mr-50 text-primary"
              />
              {{ $t('modules.products.import.steps.preview.title') }}
            </h5>
            <p class="text-muted mb-0">
              {{ $t('modules.products.import.steps.preview.description', { count: totalRows, preview: previewRows.length }) }}
            </p>
          </div>

          <b-badge
            v-if="totalRows"
            variant="light-primary"
            class="ml-1"
            pill
          >
            {{ totalRows.toLocaleString() }} {{ $t('modules.products.import.steps.preview.total_rows') }}
          </b-badge>
        </div>

        <!-- Loading state -->
        <div
          v-if="isLoading"
          class="text-center py-5"
        >
          <b-spinner variant="primary" />
          <p class="mt-1 text-muted">
            {{ $t('modules.products.import.steps.preview.loading') }}
          </p>
        </div>

        <!-- Preview table -->
        <template v-else-if="previewRows.length">
          <div class="table-responsive">
            <b-table
              :items="previewRows"
              :fields="tableFields"
              small
              bordered
              responsive
              :per-page="perPage"
              :current-page="currentPage"
              class="mb-0 import-preview-table"
            >
              <!-- Row number -->
              <template #cell(#)="data">
                <span class="text-muted small">{{ (currentPage - 1) * perPage + data.index + 1 }}</span>
              </template>

              <!-- Render image thumbnails instead of URLs -->
              <template #cell(images)="data">
                <div
                  v-if="parseImageUrls(data.value).length"
                  class="d-flex align-items-center flex-wrap"
                >
                  <a
                    v-for="(url, idx) in parseImageUrls(data.value).slice(0, 3)"
                    :key="`${url}-${idx}`"
                    :href="url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="mr-50 mb-50 d-inline-block"
                  >
                    <b-img
                      :src="url"
                      width="34"
                      height="34"
                      fluid
                      thumbnail
                      style="object-fit: cover;"
                    />
                  </a>
                </div>
                <span
                  v-else
                  class="text-muted"
                >&mdash;</span>
              </template>

              <!-- Mark cells with no value -->
              <template
                v-for="field in nonImageFields"
                #[`cell(${field.key})`]="data"
              >
                <span
                  v-if="data.value !== null && data.value !== undefined && data.value !== ''"
                  :key="field.key"
                >{{ data.value }}</span>
                <span
                  v-else
                  :key="`${field.key}-empty`"
                  class="text-muted"
                >&mdash;</span>
              </template>
            </b-table>
          </div>

          <!-- Pagination -->
          <div
            v-if="totalRows > perPage"
            class="d-flex justify-content-center mt-1"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="Math.min(totalRows, maxPreviewRows)"
              :per-page="perPage"
              size="sm"
              first-number
              last-number
            />
          </div>
        </template>

        <!-- Empty state -->
        <div
          v-else
          class="text-center py-5"
        >
          <feather-icon
            icon="InboxIcon"
            size="40"
            class="text-muted mb-1"
          />
          <p class="text-muted">
            {{ $t('modules.products.import.steps.preview.empty') }}
          </p>
        </div>

        <!-- Warning: rows capped -->
        <b-alert
          v-if="totalRows > maxPreviewRows"
          show
          variant="info"
          class="mt-1 mb-0"
        >
          <feather-icon
            icon="InfoIcon"
            class="mr-50"
          />
          {{ $t('modules.products.import.steps.preview.rows_capped', { max: maxPreviewRows, total: totalRows }) }}
        </b-alert>
      </b-card-body>
    </b-card>

    <!-- Actions -->
    <div class="d-flex justify-content-between mt-1">
      <b-button
        variant="outline-secondary"
        :disabled="isImporting"
        @click="$emit('back')"
      >
        <feather-icon
          :icon="$i18n.locale === 'ar' ? 'ArrowRightIcon' : 'ArrowLeftIcon'"
          class="mr-50"
        />
        {{ $t('modules.products.import.actions.back') }}
      </b-button>

      <b-button
        variant="success"
        :disabled="isLoading || isImporting || !previewRows.length"
        @click="$emit('import')"
      >
        <b-spinner
          v-if="isImporting"
          small
          class="mr-50"
        />
        <feather-icon
          v-else
          icon="UploadIcon"
          class="mr-50"
        />
        {{ isImporting ? $t('modules.products.import.actions.importing') : $t('modules.products.import.actions.import') }}
        <b-badge
          v-if="totalRows && !isImporting"
          variant="light"
          pill
          class="ml-50"
        >
          {{ totalRows.toLocaleString() }}
        </b-badge>
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  BAlert, BBadge, BButton, BCard, BCardBody, BImg, BPagination, BSpinner, BTable,
} from 'bootstrap-vue'
import { ref, computed } from 'vue'
import { t } from '@core/libs/i18n/utils'

export default {
  components: {
    BCard,
    BCardBody,
    BImg,
    BTable,
    BPagination,
    BBadge,
    BButton,
    BAlert,
    BSpinner,
  },

  props: {
    previewRows: {
      type: Array,
      default: () => [],
    },
    totalRows: {
      type: Number,
      default: 0,
    },
    columnMapping: {
      type: Object,
      default: () => ({}),
      // { name: 0, price: 2, ... }
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isImporting: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['back', 'import'],

  setup(props) {
    const currentPage = ref(1)
    const perPage = 25
    const maxPreviewRows = 1000

    const tableFields = computed(() => {
      const fields = [{ key: '#', label: '#', thStyle: 'width: 40px' }]
      Object.keys(props.columnMapping).forEach(fieldKey => {
        fields.push({
          key: fieldKey,
          label: t(`modules.products.import.fields.${fieldKey}`),
          sortable: false,
        })
      })
      return fields
    })

    const nonImageFields = computed(() => tableFields.value.slice(1).filter(field => field.key !== 'images'))

    const parseImageUrls = value => {
      if (!value) return []
      if (Array.isArray(value)) return value.map(v => String(v).trim()).filter(Boolean)

      return String(value)
        .split(',')
        .map(url => url.trim())
        .filter(Boolean)
    }

    return {
      currentPage,
      perPage,
      maxPreviewRows,
      tableFields,
      nonImageFields,
      parseImageUrls,
    }
  },
}
</script>

<style lang="scss" scoped>
.import-preview-table {
  font-size: 0.85rem;

  ::v-deep th {
    white-space: nowrap;
    background-color: #f3f2f7;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
}

.dark-layout .import-preview-table {
  ::v-deep th {
    background-color: #343d55;
  }
}
</style>
